import {
  ProductCatalogCompatibilityRequestModel,
  ProductCatalogCompatibilityResponse,
} from "@vp/hamilton-service-clients/dist/src/models/ProductCatalog/ProductCatalogCompatibility";
import { ProductCatalog as catalog } from "@vp/hamilton-service-clients";
import { CompatibilityCache } from "./cache/CompatibilityCache";
import { CompatibleQuantitiesCache } from "./cache/CompatibleQuantitiesCache";
import PageConfig from "../utils/PageConfig";
import RangeType from "../types/RangeTypes";

const internalCompatibilityCall = async (
  coreProductId: string,
  selections: Record<string, string>,
  useCache: boolean = true
) => {
  let compatibility = useCache
    ? CompatibilityCache.getInstance().getCompatibleValues(coreProductId, selections)
    : undefined;

  if (!compatibility) {
    const compatibleValuesRequest: ProductCatalogCompatibilityRequestModel = {
      productKey: coreProductId,
      providedValues: {
        ...selections,
      },
    };

    const config = new catalog.config(PageConfig.requestor);
    const catalogServiceClient = new catalog.serviceClient(config);

    const compatibleValuesResponse = await catalogServiceClient.getAllCompatibility(
      compatibleValuesRequest
    );

    if (compatibleValuesResponse) {
      if (useCache) {
        CompatibilityCache.getInstance().setCompatibleValues(
          coreProductId,
          selections,
          compatibleValuesResponse
        );
      }
      compatibility = compatibleValuesResponse;
    }
  }

  return compatibility;
};

const getCompatibleValues = async (props: {
  coreProductId: string;
  selections: Record<string, string>;
  productPageOptions: string[]; // This is a list of product attributes that is derived from the Product Page Merchandising Details
  useCache?: boolean;
}): Promise<ProductCatalogCompatibilityResponse | undefined> => {
  const { coreProductId, selections, productPageOptions } = props;

  // Use cache by default
  const useCache = props.useCache === undefined ? true : props.useCache;

  if (Object.keys(selections).length === 0) {
    // User has not made any selections at all
    return await internalCompatibilityCall(coreProductId, {}, useCache);
  } else {
    // At least one selection has been made
    const compatibilityCalls: any[] = [];
    productPageOptions.forEach((option: string) => {
      // Check for compatibility given the neighboring selections
      const neighboringSelections = { ...selections };
      if (neighboringSelections[`${option}`]) {
        delete neighboringSelections[`${option}`];
      }

      compatibilityCalls.push(
        internalCompatibilityCall(
          coreProductId,
          neighboringSelections,
          useCache
        ).then(compatibility => ({ compatibility, option }))
      );
    });

    const allCompatibleValues = await Promise.all(compatibilityCalls);

    // For each option displayed on the Product Builder, show compatibility based on neighboring selections
    const combinedCompatibleValues: Record<string, string[]> = {};
    const combinedCompatibleRanges: Record<string, RangeType[]> = {};
    allCompatibleValues.forEach((result: any | undefined) => {
      if (result) {
        const { compatibility, option } = result;
        combinedCompatibleValues[`${option}`] =
          compatibility?.compatibleOptions?.[`${option}`] || [];
        combinedCompatibleRanges[`${option}`] =
          compatibility?.compatibleRanges?.[`${option}`] || [];
      }
    });
    return {
      compatibleOptions: combinedCompatibleValues,
      compatibleRanges: combinedCompatibleRanges,
    };
  }
};

const getCompatibleQuantity = async (
  coreProductId: string,
  mappedChoices: Record<string, string>,
  useCache: boolean
): Promise<number[]> => {
  let compatibleQuantities = useCache
    ? CompatibleQuantitiesCache.getInstance().getCompatibleQuantities(mappedChoices)
    : undefined;

  if (!compatibleQuantities) {
    const config = new catalog.config(PageConfig.requestor);
    const catalogServiceClient = new catalog.serviceClient(config);

    compatibleQuantities = await catalogServiceClient.getMerchandisableQuantities(
      coreProductId,
      mappedChoices
    );
    compatibleQuantities = compatibleQuantities?.sort((a, b) => a - b);

    if (compatibleQuantities && useCache) {
      CompatibleQuantitiesCache.getInstance().setCompatibleQuantities(
        mappedChoices,
        compatibleQuantities
      );
    }
  }

  return compatibleQuantities ?? [];
};

export const getCompatibilityAndQuantities = async (props: {
  coreProductId: string;
  selections: Record<string, string>;
  currentCompatibleRanges: Record<string, RangeType[]>;
  currentCompatibleValues: Record<string, string[]>;
  currentCompatibleQuantities: number[];
  productPageOptions: string[];
  useCache: boolean;
}): Promise<[Record<string, string[]>, Record<string, RangeType[]>, number[]]> => {
  const {
    coreProductId,
    selections,
    currentCompatibleValues,
    currentCompatibleRanges,
    currentCompatibleQuantities,
    productPageOptions,
    useCache,
  } = props;

  // Get option compatibility
  const compatibilityCall = getCompatibleValues({
    coreProductId,
    selections,
    productPageOptions,
    useCache,
  });

  // Get quantity compatibility
  const compatibleQuantitiesCall = getCompatibleQuantity(coreProductId, selections, useCache);

  let [compatibility, compatibleQuantities] = await Promise.all([
    compatibilityCall,
    compatibleQuantitiesCall,
  ]);

  // If the compatibility call fails, then keep the current compatible options or compatible quantities
  if (!compatibility) {
    compatibility = {
      compatibleOptions: currentCompatibleValues,
      compatibleRanges: currentCompatibleRanges,
    };
  }
  if (!compatibleQuantities) {
    compatibleQuantities = currentCompatibleQuantities;
  }

  return [compatibility!.compatibleOptions, compatibility!.compatibleRanges, compatibleQuantities];
};

export { getCompatibleValues, getCompatibleQuantity };
